import { Box } from '@mui/material';
import {
  SkeletonBox,
  SnackbarApiErrorMessage,
  useDataProvider,
  useUserAccess,
  useSnackbar,
} from '@ui-framework/index';
import React, { forwardRef, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import GeneralDispatchAllRoles from './components/GeneralDispatchAllRoles';
import SPPendingAcceptanceDispatch from './components/SPPendingAcceptanceDispatch';

const statusesToShowSPView = [
  'PENDING_ACCEPTANCE',
  'ACCEPTED_WITH_CONDITIONS',
  'REJECTED',
];

const Dispatch = forwardRef((props, ref) => {
  const { getValues } = useFormContext();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const serviceJob = getValues();
  const userAccess = useUserAccess();

  const [dispatch, setDispatch] = useState({});
  const [loading, setLoading] = useState(true);
  const [isJobAssignedToSP, setIsJobAssignedToSP] = useState(false);
  const [assignedServiceProvider, setAssignedServiceProvider] = useState(null);

  const fetchDispatches = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${
      serviceJob?.id
    }/dispatches`;
    const response = await get(url);
    if (response?.ok) {
      return response.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const fetchDispatch = async dispatchId => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${
      serviceJob?.id
    }/dispatches/${dispatchId}`;
    const response = await get(url);
    if (response?.ok) {
      return response.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const fetchServiceProvider = async serviceProviderIdArg => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderIdArg}`;
    const response = await get(url);
    if (response?.ok) {
      return response?.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const initialize = async () => {
    setLoading(true);
    const dispatches = await fetchDispatches();
    if (
      dispatches &&
      Array.isArray(dispatches?.data) &&
      dispatches?.data?.length > 0
    ) {
      const dispatchId = dispatches.data[0].id;
      const currentDispatch = await fetchDispatch(dispatchId);
      if (currentDispatch) {
        setDispatch(currentDispatch);
        if (serviceJob?.serviceProviderId) {
          const spResponse = await fetchServiceProvider(
            serviceJob?.serviceProviderId,
          );
          if (spResponse) {
            // If serviceProviderId exists on serviceJob then it is assigned
            setIsJobAssignedToSP(serviceJob?.serviceProviderId);
            setAssignedServiceProvider(spResponse);
          }
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [serviceJob.id, serviceJob?.recordVersion]);

  if (loading) {
    return (
      <Box ref={ref}>
        <SkeletonBox />
      </Box>
    );
  }

  return userAccess?.isSP() &&
    statusesToShowSPView.includes(serviceJob?.dispatchEvent?.status) ? (
    <SPPendingAcceptanceDispatch dispatch={dispatch} serviceJob={serviceJob} />
  ) : (
    <GeneralDispatchAllRoles
      dispatch={dispatch}
      serviceJob={serviceJob}
      assignedServiceProvider={assignedServiceProvider}
      isJobAssignedToSP={isJobAssignedToSP}
      formPermissions={props?.formPermissions}
    />
  );
});

export default Dispatch;
